/**
 * Labstep
 *
 * @module components/Entity/EmptyState
 * @desc Generic Empty State for entities
 */

import { EMPTY_STATE_OMIT_SEARCH_PARAMS } from 'labstep-web/constants/search-params';
import { CardEmptyState } from 'labstep-web/core/Card/EmptyState';
import type { PostFilterNodeType } from 'labstep-web/models/entity-view.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import { objectOrFunction } from 'labstep-web/services/react.service';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import type React from 'react';
import type { EntityEmptyStateProps } from './types';

export const EntityEmptyState: React.FC<EntityEmptyStateProps> = ({
  entityName,
  noResultsMessage,
  noResultsMessageOnSearch,
  searchParams,
  initialSearchParams,
  action,
}) => {
  let src;
  let title;
  let explanation;

  /**
   * Filtering out the following keys which do not matter for
   * showing the empty state
   *
   * TODO Refactor into constants
   */
  const filteredSearchParams: Record<string, unknown> = omit(
    searchParams,
    EMPTY_STATE_OMIT_SEARCH_PARAMS,
  );

  // If there are no user-defined filters, remove the filter
  if (
    !(filteredSearchParams.filter as PostFilterNodeType)?.[0]
      .predicates.length
  ) {
    delete filteredSearchParams.filter;
  }

  if (searchParams.is_deleted === '1') {
    src = '/img/empty-state/files.svg';
    title = 'Nothing in your deleted items';
    explanation = `You have no deleted ${getHumanReadableEntityName(
      entityName,
      true,
    )}`;
  } else if (
    !isEmpty(filteredSearchParams) &&
    !isEqual(filteredSearchParams, initialSearchParams)
  ) {
    if (noResultsMessageOnSearch) {
      return noResultsMessageOnSearch({ searchParams });
    }
    src = '/img/empty-state/warning.svg';
    title = 'No results found';
    explanation = `We can't find any ${getHumanReadableEntityName(
      entityName,
      true,
    )} matching your criteria`;
  } else if (noResultsMessage) {
    return objectOrFunction(noResultsMessage, {
      searchParams,
    });
  } else {
    src = '/img/empty-state/warning.svg';
    title = `No ${getHumanReadableEntityName(
      entityName,
      true,
    )} created yet`;
  }

  return (
    <CardEmptyState
      src={src}
      title={title}
      explanation={explanation}
      action={action}
    />
  );
};
